import { Dropdown, Menu, Slider, Switch, Tooltip } from 'antd';
import { useRouter } from 'next/router';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Hotkeys from 'react-hot-keys';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  ArrowLeftSvg,
  ExitSvg,
  PlusSvg,
  SettingsSvg,
  UserSvg,
} from '../../../assets/icons';
import ArrowRightSvg from '../../../assets/icons/ArrowRightSvg';
import DragDropSvg from '../../../assets/icons/DragDropSvg';
import ExternalLinkSvg from '../../../assets/icons/ExternalLinkSvg';
import FlashSvg from '../../../assets/icons/FlashSvg';
import FreezeSvg from '../../../assets/icons/FreezeSvg';
import FullScreenSvg from '../../../assets/icons/FullScreenSvg';
import MinimizeSvg from '../../../assets/icons/MinimizeSvg';
import MuteSvg from '../../../assets/icons/MuteSvg';
import ProfileSvg from '../../../assets/icons/ProfileSvg';
import SkipSvg from '../../../assets/icons/SkipSvg';
import SpeakerBarlessSvg from '../../../assets/icons/SpeakerBarless';
import SpeakerHalfFullSvg from '../../../assets/icons/SpeakerHalfFull';
import SpeakerSvg from '../../../assets/icons/SpeakerSvg';
import theme from '../../../assets/theme';
import useAudio, { DEFAULT_AUDIO_VOLUME } from '../../../hooks/useAudio';
import useAutoFade from '../../../hooks/useAutoFade';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import useFullScreen from '../../../hooks/useFullScreen';
import useGameSaveSocket from '../../../hooks/useGameSaveSocket';
import useLobby from '../../../hooks/useLobby';
import { useMetrics } from '../../../hooks/useMetrics';
import { useOnOutsideClick } from '../../../hooks/useOnOutsideClick';
import usePreviewGame from '../../../hooks/usePreviewGame';
import useTemplate from '../../../hooks/useTemplate';
import { LiveActionsDisabledContext } from '../../../providers/LiveActionsDisabledProvider/LiveActionsDisabledProvider';
import { TimesyncContext } from '../../../providers/TimesyncProvider/TimesyncProvider';
import { Game, PartialParticipant } from '../../../services/backendService/types';
import { isTouchDevice } from '../../../utils/client';
import { debounceEventIfNotTrusted } from '../../../utils/general';
import {
  calculateVolume,
  findRoundSong,
  GameMusicContext,
  MusicState,
} from '../../../utils/musicUtil';
import { isSafari } from '../../../utils/navigator';
import { RoundState, toIcon } from '../../../utils/roundUtils';
import { zIndexes } from '../../../utils/zIndexes';
import { DropdownButtonItem } from '../../_atoms/DropdownButton/DropdownButton';
import { IconWrapper } from '../../_atoms/IconWrapper';
import AddNewActivity from '../../_molecules/AddNewActivity';
import BaseModal from '../../_molecules/BaseModal';
import LobbyDropdown from '../../_molecules/LobbyDropdown';
import { Slide, SlideType } from '../../SlideComponents/slide.types';
import {
  findRelatedSlidesWithoutIndex,
  getAggregatedSlidesAndActivities,
} from '../../SlideComponents/slideUtil';
import { getRoundState, toActivity } from '../LiveGame';

const LiveGameActionButtons = ({
  slides,
  showActivityMedia,
  game,
  currentSlide,
  isNavigationDisabled,
  tenantId,
  roundState,
  setShowMedia,
  setShowSettings,
  showSettings,
  showPollOptions,
  setShowPollOptions,
  participants,
  participantsCount,
}: {
  slides: Slide[];
  currentSlide: Slide;
  isNavigationDisabled: boolean;
  game: Game;
  tenantId: string;
  participants: PartialParticipant[];
  participantsCount: number;
  roundState?: RoundState;
  setShowMedia: Dispatch<SetStateAction<boolean>>;
  showActivityMedia: boolean;
  showPollOptions: boolean;
  showSettings: boolean;
  setShowSettings: Dispatch<SetStateAction<boolean>>;
  setShowPollOptions: Dispatch<SetStateAction<boolean>>;
  setRoundState?: Dispatch<SetStateAction<RoundState | undefined>>;
}) => {
  const router = useRouter();
  const { autoSave } = useGameSaveSocket();

  const { setShowLobby, lobbyHasBeenShown, showLobby } = useLobby(game?.id);

  const isLargerThan500 = useBreakPoint(500);
  const metrics = useMetrics();
  const { template, autoSaveTemplate, isTemplateOwner } = useTemplate(
    game.templateId,
    game.isFromDiscover,
  );
  const { isPreview } = usePreviewGame();
  const { now } = useContext(TimesyncContext);

  const numberOfSlides = useMemo(() => {
    return getAggregatedSlidesAndActivities(slides || []).length;
  }, [slides]);

  const currentRelatedSlide = useMemo(() => {
    return currentSlide
      ? findRelatedSlidesWithoutIndex(currentSlide.id, slides)[0]
      : undefined;
  }, [currentSlide, slides]);
  const currentRelatedSlideIndex = useMemo(() => {
    const activityAndContentSlides = getAggregatedSlidesAndActivities(slides || []);
    return currentRelatedSlide
      ? activityAndContentSlides.indexOf(currentRelatedSlide)
      : 0;
  }, [currentRelatedSlide, slides]);
  const [isHovering, setIsHovering] = useState(false);
  const { isInvisible } = useAutoFade({
    delay: 1000 * 10,
    eventListenerDependency: router.pathname,
    callback: () => setShowSettings(false),
  });
  const showLobbyTooltip = useMemo(() => !lobbyHasBeenShown && !isInvisible, [
    lobbyHasBeenShown,
    isInvisible,
  ]);
  const canShowMediaButton = useMemo(() => {
    const type = currentSlide?.type;
    if (type === 'responsegrid') return true;
    if (type === 'podium') return true;
    if (type === 'wordcloud' && roundState === 'post') return true;
    if (type === 'poll' && roundState === 'post') return true;
    return false;
  }, [currentSlide, roundState]);
  const { closeFullScreen } = useFullScreen();

  const handleOpenCurimote = () => {
    metrics.logGameEvent(
      'Curimote.Open',
      {
        gameId: game.id,
      },
      isPreview,
    );
    closeFullScreen(() => {
      window.open(
        `${window.location.origin}/${tenantId}/games/${game.id}/curimote`,
        'curimote',
        `width=${window.innerWidth * 0.6},height=${window.innerHeight * 0.6}`,
      );
    });
  };

  const handleKPress = () => {
    handleOpenCurimote();
  };

  const handleSave = (gameToSave: Game, nextSlideId: string) => {
    autoSave({
      game: gameToSave,
    });
    // saves the current round on the template in order to navigate to corret round in creator
    if (isTemplateOwner) {
      const templateSlide = template?.content.slides?.find(s => s.id === nextSlideId);
      if (templateSlide && template?.content.activeGame?.isOngoing === false) {
        autoSaveTemplate({
          templateConfig: {
            // make sure isPreview doesnt alter activeGame
            currentRound: templateSlide.id,
            id: game.templateId,
            modifiedAt: new Date().getTime(),
          },
        });
      }
    }
    setShowMedia(false);
  };
  const handleSkipBack = () => {
    if (!isNavigationDisabled && currentRelatedSlideIndex > 0 && currentSlide) {
      const currentRelatedSlides = findRelatedSlidesWithoutIndex(currentSlide.id, slides);
      const firstRelatedSlide = currentRelatedSlides[0];
      const firstRelatedSlideIndex = slides.findIndex(
        slide => slide.id === firstRelatedSlide.id,
      );
      const previousSlide = slides[firstRelatedSlideIndex - 1];
      const related = findRelatedSlidesWithoutIndex(previousSlide.id, slides);
      const wasPlayed = related.some(s => s.wasPlayed);
      const newSlide = related[wasPlayed ? related.length - 1 : 0];
      const gameToSave = {
        ...game,
        currentRound: newSlide.id,
        roundStartsAt: null,
        modifiedAt: now(),
      };
      handleSave(gameToSave, newSlide.id);
    }
  };

  const handleSkipForward = () => {
    if (
      !isNavigationDisabled &&
      currentRelatedSlideIndex + 1 < numberOfSlides &&
      currentSlide
    ) {
      const currentRelatedSlides = findRelatedSlidesWithoutIndex(currentSlide.id, slides);
      const lastRelatedSlide = currentRelatedSlides[currentRelatedSlides.length - 1];
      const lastRelatedRoundIndex = slides.findIndex(
        slide => slide.id === lastRelatedSlide.id,
      );
      const nextSlide = slides[lastRelatedRoundIndex + 1];
      const related = findRelatedSlidesWithoutIndex(nextSlide.id, slides);
      const wasPlayed = related.some(r => r.wasPlayed);
      const updatedSlides = slides.map(r =>
        r.id === currentSlide.id && (r.type === 'podium' || r.type === 'responsegrid')
          ? { ...r, wasPlayed: true }
          : r,
      );

      const gameToSave = {
        ...game,
        currentRound: related[wasPlayed ? related.length - 1 : 0].id,
        roundStartsAt: null,
        slides: updatedSlides,
        modifiedAt: now(),
      };
      handleSave(gameToSave, nextSlide.id);
    }
  };

  const handleEscapePress = () => {
    if ((roundState === 'pre' || roundState === 'post') && !showLobby) {
      metrics.logEvent('LiveGame.Escape.Pressed');
      if (!isPreview) {
        //Played from discover
        if (game?.isFromDiscover) {
          router.push(`/discover/${game.templateId}`);
        } else if (isTemplateOwner) {
          router.push(`/${tenantId}/lessons/${game?.templateId}/edit`);
        } else {
          router.push(`/${tenantId}/lessons/${game?.templateId}`);
        }
      }
    }
  };
  const handleFreezePress = () => {
    metrics.logEvent('LiveGame.Settings.Freeze.Clicked', {
      participantsCount,
    });
    if (game.roundFreezedAt) {
      if (!game.roundStartsAt) {
        // you have unfreezed the slide before you startedt it
        const gameToSave = {
          ...game,
          roundFreezedAt: null,
        };
        autoSave({
          game: gameToSave,
        });
      } else {
        // you have unfreezed the slide during an ongoing slide
        const durationFreezed = now() - game.roundFreezedAt;
        const updatedRoundStartsAt = game.roundStartsAt + durationFreezed;
        const gameToSave = {
          ...game,
          roundStartsAt: updatedRoundStartsAt,
          roundFreezedAt: null,
        };
        autoSave({
          game: gameToSave,
        });
      }
    } else {
      // you have freezed the slide
      const gameToSave = {
        ...game,
        roundFreezedAt: now(),
      };
      autoSave({
        game: gameToSave,
      });
    }
  };

  const relatedSlides = useMemo(
    () =>
      currentSlide && game.slides
        ? findRelatedSlidesWithoutIndex(currentSlide.id, game.slides)
        : undefined,
    [currentSlide, game.slides],
  );

  const handleSkipVoting = useCallback(() => {
    if (game.slides && relatedSlides) {
      metrics.logEvent('LiveGame.SkipVoting.Clicked');
      const slideIndex = game.slides.findIndex(r => r.id === currentSlide.id);
      const relatedSlideIds = relatedSlides.map(r => r.id);
      const filteredSlides = (game.slides || []).filter(
        r => !relatedSlideIds.includes(r.id),
      );

      const newSlides: Slide[] = [
        relatedSlides[0],
        {
          ...relatedSlides[relatedSlides.length - 1],
          dataReferenceSlideId: relatedSlides[0].id,
          type: 'responsegrid',
        },
      ];

      filteredSlides.splice(slideIndex, 0, ...newSlides);
      autoSave({
        game: {
          ...game,
          slides: filteredSlides,
          modifiedAt: now(),
          currentRound: newSlides[1].id,
          roundFreezedAt: null,
        },
      });
    }
  }, [game, relatedSlides, metrics, autoSave, now, currentSlide.id]);
  const { showMediaIsDisabled } = useContext(LiveActionsDisabledContext);
  return (
    <>
      <Hotkeys keyName="k" onKeyUp={handleKPress} />
      <Hotkeys
        keyName="l"
        onKeyUp={() => {
          if (!showLobby) {
            metrics.logEvent('LiveGame.Lobby.Open', { byLClick: true });
          } else {
            metrics.logEvent('LiveGame.Lobby.Close', { byLClick: true });
          }
          setShowLobby(!showLobby);
        }}
      />
      <LobbyDropdown
        setShowLobby={setShowLobby}
        game={game}
        showLobby={showLobby}
        tenantId={tenantId}
        participants={participants}
        participantsCount={participantsCount}
      />
      <LiveGameActionButtonContainer
        $isInvisible={isInvisible && !isHovering}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {currentRelatedSlide?.type === 'poll' && roundState === 'pre' && (
          <PollOptionAction
            showPollOptions={showPollOptions}
            setShowPollOptions={setShowPollOptions}
          />
        )}
        {canShowMediaButton && !showMediaIsDisabled && (
          <ShowMediaAction showMedia={showActivityMedia} setShowMedia={setShowMedia} />
        )}
        {(currentSlide.type === 'answers' || currentSlide.type === 'canvas') &&
          !currentSlide.preventSkipVotingRound &&
          roundState === 'post-between' &&
          relatedSlides &&
          relatedSlides.length > 2 && (
            <SkipVotingAction handleSkipVoting={handleSkipVoting} />
          )}
        <FreezeAction
          handleFreezePress={handleFreezePress}
          isActive={!!game.roundFreezedAt}
        />

        <ParticipantsAction
          showLobbyTooltip={showLobbyTooltip}
          onClick={() => {
            metrics.logEvent('LiveGame.Settings.Lobby.Open');
            metrics.logEvent('LiveGame.Lobby.Open', { byParticipantsAction: true });
            setShowLobby(!showLobby);
          }}
          participantsCount={participantsCount}
        />

        {isLargerThan500 && <VolumeAction game={game} showLobby={showLobby} />}

        <SettingsAction
          game={game}
          tenantId={tenantId}
          currentSlide={currentRelatedSlide}
          showSettings={showSettings && !isInvisible}
          setShowSettings={setShowSettings}
          isTemplateOwner={!!isTemplateOwner}
        />
        <LiveGameWidgetDivider />
        <LiveGameNavigationActions
          isNavigationDisabled={isNavigationDisabled}
          currentSlideType={currentRelatedSlide?.type}
          numberOfSlides={numberOfSlides}
          currentSlideRoundIndex={currentRelatedSlideIndex}
          handleEscPressed={handleEscapePress}
          handleSkipBack={() => {
            metrics.logEvent('LiveGame.GoBack.Clicked');
            if (showLobby) {
              setShowLobby(false);
            } else {
              handleSkipBack();
              setShowPollOptions(false);
            }
          }}
          handleSkipForward={() => {
            metrics.logEvent('LiveGame.GoForward.Clicked');
            if (showLobby) {
              metrics.logEvent('LiveGame.Lobby.Close', { byForwardClick: true });
              setShowLobby(false);
            } else {
              handleSkipForward();
              setShowPollOptions(false);
            }
          }}
        />
      </LiveGameActionButtonContainer>
    </>
  );
};

const SettingsAction = ({
  game,
  tenantId,
  isTemplateOwner,
  currentSlide,
  setShowSettings,
  showSettings,
}: {
  game: Game;
  showSettings: boolean;
  setShowSettings: Dispatch<SetStateAction<boolean>>;
  isTemplateOwner: boolean;
  tenantId: string;
  currentSlide?: Slide;
}) => {
  const metrics = useMetrics();
  const isDesktop = useBreakPoint(1000);
  const { now } = useContext(TimesyncContext);
  const roundState = getRoundState({ game, now });
  const { autoSave } = useGameSaveSocket();
  const containerRef = useRef(null);
  useOnOutsideClick(containerRef, () => setShowSettings(false), {
    classNameMatch: ['ant-dropdown-menu-item'],
  });

  const { closeFullScreen } = useFullScreen();
  const router = useRouter();

  const { isPreview } = usePreviewGame();
  const { t } = useTranslation('Host');
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const curimoteLink = useMemo(() => {
    return window
      ? `${window.location.origin}/${tenantId}/games/${game.id}/curimote`
      : undefined;
  }, [game.id, tenantId]);

  const handleRouteBack = useCallback(() => {
    if (game?.isFromDiscover) {
      router.push(`/discover/${game.templateId}`);
    } else if (isTemplateOwner) {
      router.push(`/${tenantId}/lessons/${game?.templateId}/edit`);
    } else {
      router.push(`/${tenantId}/lessons/${game?.templateId}`);
    }
  }, [game?.isFromDiscover, game.templateId, isTemplateOwner, router, tenantId]);

  const handleExit = useCallback(() => {
    metrics.logGameEvent('LiveQ&A.Exit', { isFromDiscover: game.isFromDiscover });
    closeFullScreen();
    if (game.isShowingLobby) {
      autoSave({
        game: { ...game, isShowingLobby: false },
        onSuccessFunction: () => {
          handleRouteBack();
        },
      });
    } else {
      handleRouteBack();
    }
  }, [autoSave, closeFullScreen, game, handleRouteBack, metrics]);

  const settingsMenu = useCallback(
    () => (
      <Menu
        style={{
          backgroundColor: 'transparent',
          padding: 0,
          paddingBottom: '10px',
          boxShadow: 'none',
          width: isDesktop ? 400 : 300,
        }}
      >
        <MenuContainer ref={containerRef} style={{ paddingTop: 10, paddingBottom: 10 }}>
          {!isPreview && (
            <MenuItem key="exit" onClick={handleExit}>
              <DropdownButtonItem
                isDarkTheme={true}
                icon={<ExitSvg />}
                title={t('LiveGameExit', 'Exit')}
                description={t('Exit the lesson and go back to the creator')}
              />
            </MenuItem>
          )}

          <FullScreenAction />
          <MenuItem
            onClick={() => {
              setShowAddActivityModal(true);
              setShowSettings(false);
              metrics.logEvent('LiveGame.Settings.AddActivity.Show');
            }}
          >
            <DropdownButtonItem
              isDarkTheme={true}
              icon={<PlusSvg />}
              title={t('Add activity')}
              description={t('Add a new activity on the fly')}
            />
          </MenuItem>
          <MenuItem key="enablenames" style={{ height: '70px' }}>
            <DropdownButtonItem
              icon={<UserSvg />}
              isDarkTheme={true}
              title={t('Require real names')}
              description={
                game.requireRealNames
                  ? t(
                      'ParticipantsWriteTheirOwn',
                      'Participants write their own real names when they join. Names are visible only in the moderation tool. They also get random nicknames.',
                    )
                  : t('Participants are assigned random nicknames when they join')
              }
              includeSwitch
              switchValue={game.requireRealNames}
              onSwitchToggled={bool => {
                metrics.logEvent('Game.ToggleRealNames', {
                  value: bool,
                  gameId: game.id,
                });
                if (game) {
                  autoSave({
                    game: {
                      ...game,
                      requireRealNames: bool,
                      modifiedAt: now(),
                    },
                  });
                }
              }}
            />
          </MenuItem>

          <MenuItem>
            <DropdownButtonItem
              isDarkTheme={true}
              showOptions
              icon={<ExternalLinkSvg />}
              title={t('Open moderation')}
              qrLink={curimoteLink}
              onCustomClick={() => {
                setShowSettings(false);
                closeFullScreen(() => {
                  metrics.logEvent('LiveGame.Settings.Curimote.Open');
                  window.open(
                    `${window.location.origin}/${tenantId}/games/${game.id}/curimote`,
                    'curimote',
                    `width=${window.innerWidth * 0.6},height=${window.innerHeight * 0.6}`,
                  );
                });
              }}
              pin={game.pin || undefined}
            />
          </MenuItem>
        </MenuContainer>
      </Menu>
    ),
    [
      isDesktop,
      isPreview,
      handleExit,
      t,
      game,
      curimoteLink,
      setShowSettings,
      metrics,
      autoSave,
      now,
      closeFullScreen,
      tenantId,
    ],
  );
  return (
    <>
      <BaseModal
        visible={showAddActivityModal}
        onCancel={() => setShowAddActivityModal(false)}
      >
        <AddNewActivity
          gameId={game.id}
          roundState={roundState}
          currentSlide={currentSlide}
          setShow={setShowAddActivityModal}
        />
      </BaseModal>
      <Dropdown visible={showSettings} placement="top" overlay={settingsMenu}>
        <LiveGameActionButton
          onClick={() => setShowSettings(e => !e)}
          className="ant-dropdown-menu-item"
          $isActive={showSettings}
        >
          <IconWrapper width="25px" height="25px">
            <SettingsSvg />
          </IconWrapper>
        </LiveGameActionButton>
      </Dropdown>
    </>
  );
};
const FullScreenAction = () => {
  const metrics = useMetrics();
  const { isFullScreen, closeFullScreen, goFullScreen } = useFullScreen();
  const toggleFullScreen = () => {
    if (!isPreview) {
      metrics.logEvent('LiveGame.FullScreen.Clicked');
      if (isFullScreen) {
        closeFullScreen();
      } else {
        goFullScreen();
      }
    }
  };
  const { t } = useTranslation('Host');
  const { isPreview } = usePreviewGame();

  return (
    <Tooltip
      title={
        isPreview
          ? t('Disabled in preview')
          : isFullScreen
          ? t('Close full screen')
          : t('Open full screen')
      }
      placement={'top'}
      mouseEnterDelay={2}
    >
      <MenuItem onClick={() => toggleFullScreen()}>
        <DropdownButtonItem
          isDarkTheme={true}
          icon={isFullScreen ? <MinimizeSvg /> : <FullScreenSvg />}
          title={isFullScreen ? t('Close fullscreen') : t('Open fullscreen')}
        />
      </MenuItem>
    </Tooltip>
  );
};

const ShowMediaAction = ({
  showMedia,
  setShowMedia,
}: {
  showMedia: boolean;
  setShowMedia: Dispatch<SetStateAction<boolean>>;
}) => {
  const metrics = useMetrics();
  return (
    <>
      <LiveGameActionButton
        style={{
          borderColor: theme.colors.white,
          color: theme.colors.white,
          display: 'flex',
          justifyContent: 'space-evenly',
          alignItems: 'center',
        }}
        onClick={() => {
          metrics.logEvent('LiveGame.Settings.ShowMedia.Clicked');
          setShowMedia(e => !e);
        }}
      >
        <IconWrapper width="25px" height="25px" style={{ marginRight: '10px' }}>
          {showMedia ? <FlashSvg /> : <DragDropSvg />}
        </IconWrapper>
        <span>{showMedia ? 'Show results' : 'Show Media'}</span>
      </LiveGameActionButton>
      <LiveGameWidgetDivider />
    </>
  );
};
const FreezeAction = ({
  handleFreezePress,
  isActive,
}: {
  handleFreezePress: () => void;
  isActive: boolean;
}) => {
  return (
    <>
      <FreezeLiveActionButton
        $active={isActive}
        onClick={() => {
          handleFreezePress();
        }}
      >
        <IconWrapper width="25px" height="25px" style={{ marginRight: '10px' }}>
          <FreezeSvg />
        </IconWrapper>
        <span>{isActive ? 'Unfreeze' : 'Freeze'}</span>
      </FreezeLiveActionButton>
      <LiveGameWidgetDivider />
    </>
  );
};

const SkipVotingAction = ({ handleSkipVoting }: { handleSkipVoting: () => void }) => {
  return (
    <>
      <LiveGameActionButton
        onClick={e => {
          debounceEventIfNotTrusted({
            cb: handleSkipVoting,
            isTrusted: e.isTrusted,
          });
        }}
      >
        <IconWrapper width="20px" height="25px" style={{ marginRight: '10px' }}>
          <SkipSvg />
        </IconWrapper>
        <span>Skip voting</span>
      </LiveGameActionButton>
      <LiveGameWidgetDivider />
    </>
  );
};

const PollOptionAction = ({
  showPollOptions,
  setShowPollOptions,
}: {
  showPollOptions: boolean;
  setShowPollOptions: Dispatch<SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation('Host');
  const isLargerThan700 = useBreakPoint(700);
  const metrics = useMetrics();
  return (
    <>
      <LiveGameActionButton
        onClick={() => {
          metrics.logEvent('LiveGame.Settings.PollOptions.Clicked');
          setShowPollOptions(!showPollOptions);
        }}
      >
        <Switch checked={showPollOptions} />
        {isLargerThan700 && (
          <span style={{ marginLeft: '5px' }}>{t('Show options')}</span>
        )}
      </LiveGameActionButton>
      <LiveGameWidgetDivider />
    </>
  );
};
const ParticipantsAction = ({
  onClick,
  participantsCount,
  showLobbyTooltip,
}: {
  onClick: () => void;
  participantsCount: number;
  showLobbyTooltip: boolean;
}) => {
  return (
    <LiveGameActionButton
      onClick={() => onClick()}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Tooltip
        title="Click here to let your students join"
        placement="top"
        color={theme.colors.green}
        visible={showLobbyTooltip}
      >
        <IconWrapper color={theme.colors.white} height="25px" width={'25px'}>
          <ProfileSvg />
        </IconWrapper>
      </Tooltip>

      <span style={{ margin: '0 2px', color: theme.colors.white, fontSize: '16px' }}>
        {participantsCount || 0}
      </span>
    </LiveGameActionButton>
  );
};

const VolumeAction = ({ game, showLobby }: { game: Game; showLobby: boolean }) => {
  const { now } = useContext(TimesyncContext);
  const [music, setMusic] = useState<MusicState>({
    autoplay: true,
    isLooping: true,
    song: undefined,
  });
  const [percievedVolume, setPercievedVolume] = useState(
    window.location.hostname === 'localhost' ? 0 : 0.6,
  );
  const isLargerthan800 = useBreakPoint(800);

  const [lastVolume, setLastVolume] = useState(DEFAULT_AUDIO_VOLUME);
  const metrics = useMetrics();
  const { isPreview } = usePreviewGame();
  const { volume, setVolume, playOrPauseAudio } = useAudio(
    music.song,
    music.autoplay,
    music.isLooping,
  );
  const useClickDropdown = isTouchDevice() && !isLargerthan800;

  const handleMPress = () => {
    handleToggle();
  };

  const handleToggle = () => {
    if (isSafari) {
      playOrPauseAudio(volume !== 0 ? false : true);
    }
    if (volume !== 0) {
      setLastVolume(percievedVolume);
      setVolume(0);
      setPercievedVolume(0);
    } else {
      setVolume(calculateVolume(lastVolume));
      setPercievedVolume(lastVolume);
      setLastVolume(0);
    }
    // setMusic(m => ({ ...m, isPlaying: newState }));
    metrics.logGameEvent('LiveQ&A.Music.Toggle', { isPlaying: volume > 0 }, isPreview);
  };

  const handleVolumeChange = (vol: number) => {
    setVolume(calculateVolume(vol));
    setPercievedVolume(vol);
  };
  useEffect(() => {
    if (game) {
      const nextMusic = findRoundSong(game, now, showLobby);
      if (nextMusic === null && music.song !== undefined) {
        // stop playing
        setMusic(m => ({
          ...m,
          isLooping: false,
          song: undefined,
        }));
        return;
      }
      if (nextMusic && nextMusic.song !== music.song) {
        setMusic(m => ({
          ...m,
          isLooping: nextMusic.loop,
          song: nextMusic.song,
        }));
      }
    }
  }, [game, music, now, showLobby]);
  return (
    <GameMusicContext.Provider value={{ music, setMusic }}>
      <Hotkeys keyName="m" onKeyUp={handleMPress} />
      <DropDownButtonContainer id="volumecontainer">
        <Dropdown
          placement="top"
          trigger={useClickDropdown ? ['click'] : ['hover']}
          overlay={() =>
            !isSafari ? (
              <div style={{ backgroundColor: 'transparent', padding: '20px' }}>
                <SliderContainer>
                  <Slider
                    reverse={false}
                    vertical
                    id="volume-slider"
                    tipFormatter={null}
                    value={percievedVolume}
                    onChange={vol => {
                      handleVolumeChange(vol);
                    }}
                    min={0}
                    max={1}
                    step={0.01}
                  />
                </SliderContainer>
              </div>
            ) : (
              <></>
            )
          }
        >
          <LiveGameActionButton onClick={handleToggle}>
            <IconWrapper color={theme.colors.white} width={'25px'} height={'25px'}>
              {volume !== 0 ? (
                volume > 0 ? (
                  volume > 0.8 ? (
                    <SpeakerSvg />
                  ) : (
                    <SpeakerHalfFullSvg />
                  )
                ) : (
                  <SpeakerBarlessSvg />
                )
              ) : (
                <MuteSvg />
              )}
            </IconWrapper>
          </LiveGameActionButton>
        </Dropdown>
      </DropDownButtonContainer>
    </GameMusicContext.Provider>
  );
};

export const LiveGameNavigationActions = ({
  currentSlideType,
  numberOfSlides,
  currentSlideRoundIndex,
  isNavigationDisabled,
  handleSkipBack,
  handleSkipForward,
  handleEscPressed,
}: {
  currentSlideType?: SlideType;
  numberOfSlides: number;
  currentSlideRoundIndex: number;
  isNavigationDisabled: boolean;
  handleSkipBack?: () => void;
  handleSkipForward?: () => void;
  handleEscPressed?: () => void;
}) => {
  const { t } = useTranslation('Host');
  const isLargerThan900 = useBreakPoint(900);

  const { arrowKeyIsDisabled } = useContext(LiveActionsDisabledContext);
  return (
    <NavigationButtons>
      {arrowKeyIsDisabled !== true && (
        <>
          <Hotkeys keyName="left" onKeyUp={handleSkipBack} />
          <Hotkeys keyName="right" onKeyUp={handleSkipForward} />
        </>
      )}
      <Hotkeys keyName="Escape" onKeyUp={() => handleEscPressed && handleEscPressed()} />
      <LiveGameActionButton
        $disabled={currentSlideRoundIndex < 1 || isNavigationDisabled}
        style={{ marginRight: '2px' }}
        onClick={handleSkipBack}
      >
        <IconWrapper
          color="white"
          width="40px"
          height="40px"
          style={{
            cursor: currentSlideRoundIndex < 1 ? 'not-allowed' : 'pointer',
          }}
        >
          <ArrowLeftSvg />
        </IconWrapper>
      </LiveGameActionButton>

      <NavigateSlideTitle className={'slide-title'}>
        {(currentSlideType !== 'slide' || !isLargerThan900) && (
          <IconWrapper
            color="white"
            style={{ marginRight: '2px', marginBottom: '-5px' }}
            width={'20px'}
            height={'20px'}
          >
            {toIcon(currentSlideType || '').icon}
          </IconWrapper>
        )}

        <span style={{ marginBottom: '-5px' }}>
          {isLargerThan900 && toActivity(t, currentSlideType || '')}
        </span>

        <span
          style={{
            whiteSpace: 'nowrap',
            width: '100%',
            fontSize: '16px',
            marginTop: '-5px',
          }}
        >
          {currentSlideRoundIndex + 1}/{numberOfSlides}
        </span>
      </NavigateSlideTitle>
      <LiveGameActionButton
        style={{ marginRight: '12px' }}
        $disabled={currentSlideRoundIndex >= numberOfSlides - 1 || isNavigationDisabled}
        onClick={() => {
          if (handleSkipForward) handleSkipForward();
        }}
      >
        <IconWrapper
          color="white"
          width="40px"
          height="40px"
          style={{
            cursor:
              currentSlideRoundIndex >= numberOfSlides - 1 ? 'not-allowed' : 'pointer',
          }}
        >
          <ArrowRightSvg />
        </IconWrapper>
      </LiveGameActionButton>
    </NavigationButtons>
  );
};

const MenuContainer = styled.div`
  display: grid;
  grid-auto-flow: row;
  background-color: ${theme.colors.translucentLightBlack};
  border-radius: 10px;
  box-shadow: 0px 4px 4px rgb(0, 0, 0, 0.8);
`;
const MenuItem = styled(Menu.Item)`
  background-color: transparent !important;
  color: white;
  opacity: 0.9;

  &:hover {
    opacity: 1;
  }
`;
export const LiveGameActionButton = styled.button<{
  $isActive?: boolean;
  $disabled?: boolean;
}>`
  border: none;
  transition-duration: 0.5s;
  min-height: 30px;
  height: 100%;
  padding: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-width: 60px;
  border-radius: 10px;
  font-size: 1.2em;
  color: white;
  background-color: ${({ $isActive }) => ($isActive ? '#656570;' : 'transparent')};
  filter: ${({ $disabled }) => ($disabled ? 'brightness(50%)' : 'brightness(100%)')};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  svg {
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  }
  outline: none;
  &:hover {
    background-color: #656570;
  }
`;
export const LiveGameActionButtonContainer = styled.div<{ $isInvisible: boolean }>`
  opacity: ${({ $isInvisible }) => ($isInvisible ? '0' : '1')};
  transition: 0.3s;
  position: fixed;
  bottom: 10px;
  transform: translateX(0%);
  right: 10px;
  justify-content: space-evenly;
  font-size: 12px;
  display: grid;
  align-items: center;
  gap: 12px;
  grid-auto-flow: column;
  justify-content: space-evenly;

  background-color: ${theme.colors.translucentLightBlack};
  padding: 5px;
  padding-left: 5px;
  padding-right: 5px;
  border-radius: 10px;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.25);
  z-index: ${zIndexes('PRESENTER_ACTION_BUTTONS')};
  @media (max-width: 900px) {
    gap: 2px;
  }
`;
const DropDownButtonContainer = styled.div`
  transition: height, 0.1s, transform 0.2s;
  border-radius: 10px;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: hidden;
  background-color: 'transparent';//${theme.colors.translucentLightBlack};
`;
const SliderContainer = styled.div`
  z-index: ${zIndexes('PRESENTER_ACTION_BUTTON_MUSIC_SLIDER')};
  background-color: ${theme.colors.greyDark};
  height: 150px;
  border-radius: 10px;
  padding: 10px;
  padding-top: 20px;
  padding-bottom: 20px;
`;
const NavigationButtons = styled.div`
  display: flex;
  align-items: center;
  height: 50px;
  color: white;
`;

const LiveGameWidgetDivider = styled.div`
  margin: 0;
  border-right: solid 1px ${theme.colors.grey};
  height: 100%;
`;

const NavigateSlideTitle = styled.div`
  width: 170px;
  display: flex;
  text-align: center;
  height: 100%;
  flex-wrap: wrap;
  justify-content: center;
  font-size: 20px;
  align-items: center;
  cursor: default;
  svg {
    cursor: default;
  }
  font-weight: bold;
  @media (max-width: 900px) {
    width: 40px;
  }
`;

const FreezeLiveActionButton = styled(LiveGameActionButton)<{ $active: boolean }>`
  display: flex;
  color: ${theme.colors.white};
  position: relative;
  overflow: hidden;
  padding: 0px 10px;
  background-color: transparent;
  &:after {
    background: #fff;
    content: '';
    height: 155px;
    left: -75px;
    opacity: 0.2;
    position: absolute;
    top: -50px;
    transform: rotate(35deg);
    transition: all 550ms cubic-bezier(0.19, 1, 0.22, 1);
    width: 50px;
    z-index: -10;
  }
  ${({ $active }) => $active && 'background-color: rgba(51, 160, 221, 0.5)'};
  &:hover {
    background-color: rgba(51, 160, 221, 0.5);
    &:after {
      left: 120%;
      transition: all 550ms cubic-bezier(0.19, 1, 0.22, 1);
    }
  }
`;
export default LiveGameActionButtons;
